import type { OfflineTextCatalog } from "@/v2-text/offline/types"

/**
 * Bundled, translated UI-chrome catalog — the strings that CANNOT come from
 * the async DB fetch: loading/empty/error states (you can't fetch the word
 * "Loading" while waiting for the fetch) and base `v2-ui` primitives that
 * mount on screens above the console providers (login, auth-error, error
 * boundaries, tests, other apps). Resolved synchronously against the locale
 * store (`../locale`) with no `<TextProvider>` and no loading state.
 *
 * Shape mirrors the features DB `[Language]` JSON exactly:
 * `{ <code>: { "<tag>": { Name, Description? } } }`. `en-GB` is the required
 * base/fallback (as in `common.LanguageJson`); other tags are optional and
 * fall back to it. Set-once data — a single flat catalog with every language
 * colocated per key, so a translation script can update one file wholesale.
 */
export const PRIMITIVE_TEXT_CATALOG = {
  // --- breadcrumb ---
  UI_Primitive_Breadcrumb_More: {
    "en-GB": { Name: "More" },
    "nb-NO": { Name: "Mer" },
    "da-DK": { Name: "Mere" },
    "nn-NO": { Name: "Meir" },
    "sv-SE": { Name: "Mer" },
  },

  // --- carousel ---
  UI_Primitive_Carousel_PreviousSlide: {
    "en-GB": { Name: "Previous slide" },
    "nb-NO": { Name: "Forrige bilde" },
    "da-DK": { Name: "Forrige billede" },
    "nn-NO": { Name: "Førre bilde" },
    "sv-SE": { Name: "Föregående bild" },
  },
  UI_Primitive_Carousel_NextSlide: {
    "en-GB": { Name: "Next slide" },
    "nb-NO": { Name: "Neste bilde" },
    "da-DK": { Name: "Næste billede" },
    "nn-NO": { Name: "Neste bilde" },
    "sv-SE": { Name: "Nästa bild" },
  },

  // --- color/palette ---
  UI_Primitive_ColorPalette_Empty: {
    "en-GB": { Name: "No colormaps found." },
    "nb-NO": { Name: "Fant ingen fargekart." },
    "da-DK": { Name: "Fandt ingen farvekort." },
    "nn-NO": { Name: "Fann ingen fargekart." },
    "sv-SE": { Name: "Hittade inga färgkartor." },
  },
  UI_Primitive_ColorPalette_TypeLabel: {
    "en-GB": { Name: "Type" },
    "nb-NO": { Name: "Type" },
    "da-DK": { Name: "Type" },
    "nn-NO": { Name: "Type" },
    "sv-SE": { Name: "Typ" },
  },
  UI_Primitive_ColorPalette_StepsLabel: {
    "en-GB": { Name: "Steps" },
    "nb-NO": { Name: "Trinn" },
    "da-DK": { Name: "Trin" },
    "nn-NO": { Name: "Trinn" },
    "sv-SE": { Name: "Steg" },
  },
  UI_Primitive_ColorPalette_ReversedLabel: {
    "en-GB": { Name: "Reversed" },
    "nb-NO": { Name: "Reversert" },
    "da-DK": { Name: "Omvendt" },
    "nn-NO": { Name: "Reversert" },
    "sv-SE": { Name: "Omvänd" },
  },
  // en-GB only — nb-NO omitted, falls back to en-GB.
  UI_Primitive_ColorPalette_ColorBlindSafeLabel: {
    "en-GB": { Name: "Color Blind Safe" },
    "da-DK": { Name: "Farveblindvenlig" },
    "nb-NO": { Name: "Fargeblindvennlig" },
    "nn-NO": { Name: "Fargeblindvennleg" },
    "sv-SE": { Name: "Färgblindanpassad" },
  },
  UI_Primitive_ColorPalette_SearchPlaceholder: {
    "en-GB": { Name: "Search colormaps..." },
    "nb-NO": { Name: "Søk i fargekart..." },
    "da-DK": { Name: "Søg i farvekort..." },
    "nn-NO": { Name: "Søk i fargekart..." },
    "sv-SE": { Name: "Sök bland färgkartor..." },
  },
  UI_Primitive_ColorPalette_TypePlaceholder: {
    "en-GB": { Name: "Select type" },
    "nb-NO": { Name: "Velg type" },
    "da-DK": { Name: "Vælg type" },
    "nn-NO": { Name: "Vel type" },
    "sv-SE": { Name: "Välj typ" },
  },
  UI_Primitive_ColorPalette_StepsPlaceholder: {
    "en-GB": { Name: "Select number of steps" },
    "nb-NO": { Name: "Velg antall trinn" },
    "da-DK": { Name: "Vælg antal trin" },
    "nn-NO": { Name: "Vel talet på trinn" },
    "sv-SE": { Name: "Välj antal steg" },
  },

  // --- color/picker ---
  UI_Primitive_ColorPickerEyedropper_PickColor: {
    "en-GB": { Name: "Pick color from screen" },
    "nb-NO": { Name: "Plukk farge fra skjermen" },
    "da-DK": { Name: "Vælg farve fra skærmen" },
    "nn-NO": { Name: "Plukk farge frå skjermen" },
    "sv-SE": { Name: "Välj färg från skärmen" },
  },

  // --- datePicker ---
  UI_Primitive_DatePicker_YearPlaceholder: {
    "en-GB": { Name: "Year" },
    "nb-NO": { Name: "År" },
    "da-DK": { Name: "År" },
    "nn-NO": { Name: "År" },
    "sv-SE": { Name: "År" },
  },
  UI_Primitive_DatePicker_MonthPlaceholder: {
    "en-GB": { Name: "Month" },
    "nb-NO": { Name: "Måned" },
    "da-DK": { Name: "Måned" },
    "nn-NO": { Name: "Månad" },
    "sv-SE": { Name: "Månad" },
  },
  UI_Primitive_DatePicker_Reset: {
    "en-GB": { Name: "Reset" },
    "nb-NO": { Name: "Nullstill" },
    "da-DK": { Name: "Nulstil" },
    "nn-NO": { Name: "Nullstill" },
    "sv-SE": { Name: "Återställ" },
  },

  // --- dialog ---
  UI_Primitive_Dialog_Close: {
    "en-GB": { Name: "Close" },
    "nb-NO": { Name: "Lukk" },
    "da-DK": { Name: "Luk" },
    "nn-NO": { Name: "Lukk" },
    "sv-SE": { Name: "Stäng" },
  },
  UI_Primitive_DialogAdvanced_Close: {
    "en-GB": { Name: "Close (esc)" },
    "nb-NO": { Name: "Lukk (esc)" },
    "da-DK": { Name: "Luk (esc)" },
    "nn-NO": { Name: "Lukk (esc)" },
    "sv-SE": { Name: "Stäng (esc)" },
  },

  // --- frame ---
  UI_Primitive_Frame_Close: {
    "en-GB": { Name: "Close" },
    "nb-NO": { Name: "Lukk" },
    "da-DK": { Name: "Luk" },
    "nn-NO": { Name: "Lukk" },
    "sv-SE": { Name: "Stäng" },
  },

  // --- errorMessage ---
  UI_Primitive_ErrorMessage_Root: {
    "en-GB": { Name: "An error occurred" },
    "nb-NO": { Name: "Feil oppstod" },
    "da-DK": { Name: "Der opstod en fejl" },
    "nn-NO": { Name: "Det oppstod ein feil" },
    "sv-SE": { Name: "Ett fel uppstod" },
  },

  // --- inlineEditInput ---
  UI_Primitive_InlineEditInput_Confirm: {
    "en-GB": { Name: "Confirm" },
    "nb-NO": { Name: "Bekreft" },
    "da-DK": { Name: "Bekræft" },
    "nn-NO": { Name: "Stadfest" },
    "sv-SE": { Name: "Bekräfta" },
  },

  // --- input/numberRange ---
  UI_Primitive_NumberRangeInput_From: {
    "en-GB": { Name: "From" },
    "nb-NO": { Name: "Fra" },
    "da-DK": { Name: "Fra" },
    "nn-NO": { Name: "Frå" },
    "sv-SE": { Name: "Från" },
  },
  UI_Primitive_NumberRangeInput_To: {
    "en-GB": { Name: "To" },
    "nb-NO": { Name: "Til" },
    "da-DK": { Name: "Til" },
    "nn-NO": { Name: "Til" },
    "sv-SE": { Name: "Till" },
  },

  // --- listLoader ---
  UI_Primitive_ListLoader_Root: {
    "en-GB": { Name: "Loading" },
    "nb-NO": { Name: "Laster" },
    "da-DK": { Name: "Indlæser" },
    "nn-NO": { Name: "Lastar" },
    "sv-SE": { Name: "Laddar" },
  },

  // --- multiSelect ---
  UI_Primitive_MultiSelect_SelectAll: {
    "en-GB": { Name: "Select all" },
    "nb-NO": { Name: "Velg alle" },
    "da-DK": { Name: "Vælg alle" },
    "nn-NO": { Name: "Vel alle" },
    "sv-SE": { Name: "Välj alla" },
  },
  UI_Primitive_MultiSelect_Clear: {
    "en-GB": { Name: "Clear" },
    "nb-NO": { Name: "Tøm" },
    "da-DK": { Name: "Ryd" },
    "nn-NO": { Name: "Tøm" },
    "sv-SE": { Name: "Rensa" },
  },
  UI_Primitive_MultiSelect_Placeholder: {
    "en-GB": { Name: "Select…" },
    "nb-NO": { Name: "Velg…" },
    "da-DK": { Name: "Vælg…" },
    "nn-NO": { Name: "Vel…" },
    "sv-SE": { Name: "Välj…" },
  },
  UI_Primitive_MultiSelect_SearchPlaceholder: {
    "en-GB": { Name: "Search…" },
    "nb-NO": { Name: "Søk…" },
    "da-DK": { Name: "Søg…" },
    "nn-NO": { Name: "Søk…" },
    "sv-SE": { Name: "Sök…" },
  },
  UI_Primitive_MultiSelect_NoMatches: {
    "en-GB": { Name: "No matches" },
    "nb-NO": { Name: "Ingen treff" },
    "da-DK": { Name: "Ingen resultater" },
    "nn-NO": { Name: "Ingen treff" },
    "sv-SE": { Name: "Inga träffar" },
  },

  // --- nivoChart ---
  UI_Primitive_NivoBarChart_Empty: {
    "en-GB": { Name: "No data available" },
    "nb-NO": { Name: "Ingen data tilgjengelig" },
    "da-DK": { Name: "Ingen data tilgængelige" },
    "nn-NO": { Name: "Ingen data tilgjengelege" },
    "sv-SE": { Name: "Inga data tillgängliga" },
  },
  UI_Primitive_NivoBarChart_LoadErrorTitle: {
    "en-GB": { Name: "Could not load chart" },
    "nb-NO": { Name: "Kunne ikke laste diagram" },
    "da-DK": { Name: "Kunne ikke indlæse diagram" },
    "nn-NO": { Name: "Kunne ikkje laste diagram" },
    "sv-SE": { Name: "Kunde inte läsa in diagram" },
  },

  // --- pagination ---
  UI_Primitive_Pagination_Previous: {
    "en-GB": { Name: "Previous" },
    "nb-NO": { Name: "Forrige" },
    "da-DK": { Name: "Forrige" },
    "nn-NO": { Name: "Førre" },
    "sv-SE": { Name: "Föregående" },
  },
  UI_Primitive_Pagination_Next: {
    "en-GB": { Name: "Next" },
    "nb-NO": { Name: "Neste" },
    "da-DK": { Name: "Næste" },
    "nn-NO": { Name: "Neste" },
    "sv-SE": { Name: "Nästa" },
  },
  UI_Primitive_Pagination_MorePages: {
    "en-GB": { Name: "More pages" },
    "nb-NO": { Name: "Flere sider" },
    "da-DK": { Name: "Flere sider" },
    "nn-NO": { Name: "Fleire sider" },
    "sv-SE": { Name: "Fler sidor" },
  },

  // --- placeholder ---
  UI_Primitive_Placeholder_Root: {
    "en-GB": { Name: "No data" },
    "nb-NO": { Name: "Ingen data" },
    "da-DK": { Name: "Ingen data" },
    "nn-NO": { Name: "Ingen data" },
    "sv-SE": { Name: "Inga data" },
  },

  // --- searchBar ---
  UI_Primitive_SearchBar_ClearSearch: {
    "en-GB": { Name: "Clear search" },
    "nb-NO": { Name: "Tøm søk" },
    "da-DK": { Name: "Ryd søgning" },
    "nn-NO": { Name: "Tøm søk" },
    "sv-SE": { Name: "Rensa sökning" },
  },

  // --- sheet ---
  UI_Primitive_Sheet_Close: {
    "en-GB": { Name: "Close" },
    "nb-NO": { Name: "Lukk" },
    "da-DK": { Name: "Luk" },
    "nn-NO": { Name: "Lukk" },
    "sv-SE": { Name: "Stäng" },
  },

  // --- sidebar ---
  // en-GB only (sr-only labels) — fall back to en-GB.
  UI_Primitive_Sidebar_MobileTitle: {
    "en-GB": { Name: "Sidebar mobile" },
    "da-DK": { Name: "Sidepanel mobil" },
    "nb-NO": { Name: "Sidefelt mobil" },
    "nn-NO": { Name: "Sidefelt mobil" },
    "sv-SE": { Name: "Sidofält mobil" },
  },
  UI_Primitive_Sidebar_MobileDescription: {
    "en-GB": { Name: "Sidebar mobile" },
    "da-DK": { Name: "Sidepanel mobil" },
    "nb-NO": { Name: "Sidefelt mobil" },
    "nn-NO": { Name: "Sidefelt mobil" },
    "sv-SE": { Name: "Sidofält mobil" },
  },
  UI_Primitive_Sidebar_Toggle: {
    "en-GB": { Name: "Toggle Sidebar" },
    "nb-NO": { Name: "Veksle sidefelt" },
    "da-DK": { Name: "Vis/skjul sidepanel" },
    "nn-NO": { Name: "Veksle sidefelt" },
    "sv-SE": { Name: "Växla sidofält" },
  },

  // --- table ---
  UI_Primitive_NumberRangeColumnFilter_Separator: {
    "en-GB": { Name: "to" },
    "nb-NO": { Name: "til" },
    "da-DK": { Name: "til" },
    "nn-NO": { Name: "til" },
    "sv-SE": { Name: "till" },
  },
  UI_Primitive_SelectColumnFilter_All: {
    "en-GB": { Name: "All" },
    "nb-NO": { Name: "Alle" },
    "da-DK": { Name: "Alle" },
    "nn-NO": { Name: "Alle" },
    "sv-SE": { Name: "Alla" },
  },

  // --- timePicker ---
  UI_Primitive_TimePicker_ClearTime: {
    "en-GB": { Name: "Clear time" },
    "nb-NO": { Name: "Tøm klokkeslett" },
    "da-DK": { Name: "Ryd klokkeslæt" },
    "nn-NO": { Name: "Tøm klokkeslett" },
    "sv-SE": { Name: "Rensa tid" },
  },
  UI_Primitive_TimePicker_HoursLabel: {
    "en-GB": { Name: "hours" },
    "nb-NO": { Name: "timer" },
    "da-DK": { Name: "timer" },
    "nn-NO": { Name: "timar" },
    "sv-SE": { Name: "timmar" },
  },
  UI_Primitive_TimePicker_MinutesLabel: {
    "en-GB": { Name: "minutes" },
    "nb-NO": { Name: "minutter" },
    "da-DK": { Name: "minutter" },
    "nn-NO": { Name: "minutt" },
    "sv-SE": { Name: "minuter" },
  },
  UI_Primitive_TimePicker_SecondsLabel: {
    "en-GB": { Name: "seconds" },
    "nb-NO": { Name: "sekunder" },
    "da-DK": { Name: "sekunder" },
    "nn-NO": { Name: "sekund" },
    "sv-SE": { Name: "sekunder" },
  },
  UI_Primitive_Breadcrumb_Label: {
    "en-GB": { Name: "Breadcrumb" },
    "da-DK": { Name: "Brødkrummesti" },
    "nb-NO": { Name: "Brødsmulesti" },
    "nn-NO": { Name: "Brødsmulesti" },
    "sv-SE": { Name: "Brödsmulor" },
  },
  UI_Primitive_Carousel_Roledescription: {
    "en-GB": { Name: "carousel" },
    "da-DK": { Name: "karrusel" },
    "nb-NO": { Name: "karusell" },
    "nn-NO": { Name: "karusell" },
    "sv-SE": { Name: "karusell" },
  },
  UI_Primitive_Carousel_SlideRoledescription: {
    "en-GB": { Name: "slide" },
    "da-DK": { Name: "dias" },
    "nb-NO": { Name: "lysbilde" },
    "nn-NO": { Name: "lysbilete" },
    "sv-SE": { Name: "bild" },
  },
  UI_Primitive_ColorPresetPicker_None: {
    "en-GB": { Name: "None" },
    "da-DK": { Name: "Ingen" },
    "nb-NO": { Name: "Ingen" },
    "nn-NO": { Name: "Ingen" },
    "sv-SE": { Name: "Ingen" },
  },
  UI_Primitive_ColorPresetPicker_Custom: {
    "en-GB": { Name: "Custom color" },
    "da-DK": { Name: "Brugerdefineret farve" },
    "nb-NO": { Name: "Egendefinert farge" },
    "nn-NO": { Name: "Eigendefinert farge" },
    "sv-SE": { Name: "Anpassad färg" },
  },
  UI_Primitive_ColorPresetPicker_Label: {
    "en-GB": { Name: "Color" },
    "da-DK": { Name: "Farve" },
    "nb-NO": { Name: "Farge" },
    "nn-NO": { Name: "Farge" },
    "sv-SE": { Name: "Färg" },
  },
  UI_Primitive_ColorSwatchSections_AddCurrentColor: {
    "en-GB": { Name: "Add current color" },
    "da-DK": { Name: "Tilføj aktuel farve" },
    "nb-NO": { Name: "Legg til gjeldende farge" },
    "nn-NO": { Name: "Legg til gjeldande farge" },
    "sv-SE": { Name: "Lägg till aktuell färg" },
  },
  UI_Primitive_Pagination_Label: {
    "en-GB": { Name: "Pagination" },
    "da-DK": { Name: "Sidenavigation" },
    "nb-NO": { Name: "Sidenavigering" },
    "nn-NO": { Name: "Sidenavigering" },
    "sv-SE": { Name: "Sidnavigering" },
  },
  UI_Primitive_NumberRangeColumnFilter_MinPlaceholder: {
    "en-GB": { Name: "Min ({value})" },
    "da-DK": { Name: "Min ({value})" },
    "nb-NO": { Name: "Min ({value})" },
    "nn-NO": { Name: "Min ({value})" },
    "sv-SE": { Name: "Min ({value})" },
  },
  UI_Primitive_NumberRangeColumnFilter_MaxPlaceholder: {
    "en-GB": { Name: "Max ({value})" },
    "da-DK": { Name: "Maks ({value})" },
    "nb-NO": { Name: "Maks ({value})" },
    "nn-NO": { Name: "Maks ({value})" },
    "sv-SE": { Name: "Max ({value})" },
  },

  // --- FUS-2561: defaults that used to be string literals in props ---
  UI_Primitive_ConfirmButton_Title: {
    "en-GB": { Name: "Are you absolutely sure?" },
    "da-DK": { Name: "Er du helt sikker?" },
    "nb-NO": { Name: "Er du helt sikker?" },
    "nn-NO": { Name: "Er du heilt sikker?" },
    "sv-SE": { Name: "Är du helt säker?" },
  },
  UI_Primitive_ConfirmButton_Description: {
    "en-GB": { Name: "This action cannot be undone." },
    "da-DK": { Name: "Denne handling kan ikke fortrydes." },
    "nb-NO": { Name: "Denne handlingen kan ikke angres." },
    "nn-NO": { Name: "Denne handlinga kan ikkje angrast." },
    "sv-SE": { Name: "Den här åtgärden kan inte ångras." },
  },
  UI_Primitive_ConfirmButton_Confirm: {
    "en-GB": { Name: "Continue" },
    "da-DK": { Name: "Fortsæt" },
    "nb-NO": { Name: "Fortsett" },
    "nn-NO": { Name: "Hald fram" },
    "sv-SE": { Name: "Fortsätt" },
  },
  UI_Primitive_ConfirmButton_Cancel: {
    "en-GB": { Name: "Cancel" },
    "da-DK": { Name: "Annuller" },
    "nb-NO": { Name: "Avbryt" },
    "nn-NO": { Name: "Avbryt" },
    "sv-SE": { Name: "Avbryt" },
  },
  UI_Primitive_ConfirmationDialog_Title: {
    "en-GB": { Name: "Are you sure?" },
    "da-DK": { Name: "Er du sikker?" },
    "nb-NO": { Name: "Er du sikker?" },
    "nn-NO": { Name: "Er du sikker?" },
    "sv-SE": { Name: "Är du säker?" },
  },
  UI_Primitive_ConfirmationDialog_Description: {
    "en-GB": { Name: "This action cannot be undone." },
    "da-DK": { Name: "Denne handling kan ikke fortrydes." },
    "nb-NO": { Name: "Denne handlingen kan ikke angres." },
    "nn-NO": { Name: "Denne handlinga kan ikkje angrast." },
    "sv-SE": { Name: "Den här åtgärden kan inte ångras." },
  },
  UI_Primitive_ConfirmationDialog_Confirm: {
    "en-GB": { Name: "Confirm" },
    "da-DK": { Name: "Bekræft" },
    "nb-NO": { Name: "Bekreft" },
    "nn-NO": { Name: "Stadfest" },
    "sv-SE": { Name: "Bekräfta" },
  },
  UI_Primitive_ConfirmationDialog_Cancel: {
    "en-GB": { Name: "Cancel" },
    "da-DK": { Name: "Annuller" },
    "nb-NO": { Name: "Avbryt" },
    "nn-NO": { Name: "Avbryt" },
    "sv-SE": { Name: "Avbryt" },
  },
  UI_Primitive_CopyButton_Label: {
    "en-GB": { Name: "Copy to clipboard" },
    "da-DK": { Name: "Kopiér til udklipsholder" },
    "nb-NO": { Name: "Kopier til utklippstavlen" },
    "nn-NO": { Name: "Kopier til utklippstavla" },
    "sv-SE": { Name: "Kopiera till urklipp" },
  },
  UI_Primitive_CustomBarMarker_Label: {
    "en-GB": { Name: "Label" },
    "da-DK": { Name: "Etiket" },
    "nb-NO": { Name: "Etikett" },
    "nn-NO": { Name: "Etikett" },
    "sv-SE": { Name: "Etikett" },
  },
  UI_Primitive_ColorPalette_SelectPlaceholder: {
    "en-GB": { Name: "Choose a color palette..." },
    "da-DK": { Name: "Vælg en farvepalet..." },
    "nb-NO": { Name: "Velg en fargepalett..." },
    "nn-NO": { Name: "Vel ein fargepalett..." },
    "sv-SE": { Name: "Välj en färgpalett..." },
  },
  UI_Primitive_DatePicker_Placeholder: {
    "en-GB": { Name: "Pick a date" },
    "da-DK": { Name: "Vælg en dato" },
    "nb-NO": { Name: "Velg en dato" },
    "nn-NO": { Name: "Vel ein dato" },
    "sv-SE": { Name: "Välj ett datum" },
  },
  UI_Primitive_Frame_FullscreenEnter: {
    "en-GB": { Name: "Make larger" },
    "nb-NO": { Name: "Gjør større" },
    "da-DK": { Name: "Gør større" },
    "nn-NO": { Name: "Gjer større" },
    "sv-SE": { Name: "Gör större" },
  },
  UI_Primitive_Frame_FullscreenExit: {
    "en-GB": { Name: "Make smaller" },
    "nb-NO": { Name: "Gjør mindre" },
    "da-DK": { Name: "Gør mindre" },
    "nn-NO": { Name: "Gjer mindre" },
    "sv-SE": { Name: "Gör mindre" },
  },
  UI_Primitive_Frame_Minimize: {
    "en-GB": { Name: "Minimize" },
    "nb-NO": { Name: "Minimer" },
    "da-DK": { Name: "Minimer" },
    "nn-NO": { Name: "Minimer" },
    "sv-SE": { Name: "Minimera" },
  },
  UI_Primitive_Frame_Expand: {
    "en-GB": { Name: "Expand" },
    "nb-NO": { Name: "Utvid" },
    "da-DK": { Name: "Udvid" },
    "nn-NO": { Name: "Utvid" },
    "sv-SE": { Name: "Expandera" },
  },
  UI_Primitive_Spinner_Loading: {
    "en-GB": { Name: "Loading..." },
    "nb-NO": { Name: "Laster.." },
    "da-DK": { Name: "Indlæser..." },
    "nn-NO": { Name: "Lastar..." },
    "sv-SE": { Name: "Laddar..." },
  },
} as const satisfies OfflineTextCatalog

export type PrimitiveTextKey = keyof typeof PRIMITIVE_TEXT_CATALOG
